import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Card,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Field,
  Input,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Radio,
  RadioGroup,
  Skeleton,
  SkeletonItem,
  Spinner,
  Subtitle1,
  makeStyles,
  tokens,
} from '@coreai/fluentui-react';
import {
  ArrowClockwiseRegular,
  DeleteRegular,
  DismissRegular,
  EditRegular,
} from '@fluentui/react-icons';
import { FeatureErrorBoundary } from '@/components/ErrorBoundary';
import { useDebounce } from '@/hooks/useDebounce';
import { useTelemetry } from '@/hooks/useTelemetry';
import { useObservabilityConfig, useUpdateObservabilityConfig, useDeleteObservabilityConfig } from '@/hooks/useObservabilityConfig';
import type { ObservabilityApiError } from '@/services/observabilityApi';
import type { AppInsightsResource } from '@/services/applicationInsightsService';
import { getRelativeTime } from '@/utils/dateTime';
import { validateAppInsightsConnectionString } from '@/utils/validation';
import { AppInsightsResourcePicker } from './AppInsightsResourcePicker';

type InputMode = 'picker' | 'paste';
/** Origin of the connection-string write. Used only for telemetry on the
 *  in-session save event — the backend does not return source, so we never
 *  attempt to surface it across reloads. */
type SavedSource = 'picker' | 'paste';

const modeStorageKey = (projectName: string) => `otto.observability.mode.${projectName}`;
const subStorageKey = (projectName: string) => `otto.observability.lastSub.${projectName}`;

const readStorage = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const writeStorage = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore quota / disabled storage */
  }
};

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  sectionHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalL,
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    paddingBlock: tokens.spacingVerticalXS,
    paddingInline: tokens.spacingHorizontalM,
    borderRadius: tokens.borderRadiusXLarge,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorPaletteGreenForeground1,
    backgroundColor: tokens.colorPaletteGreenBackground1,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorPaletteGreenBorder2}`,
    '@media (forced-colors: active)': {
      color: 'CanvasText',
      backgroundColor: 'Canvas',
      border: `${tokens.strokeWidthThin} solid CanvasText`,
    },
  },
  statusDot: {
    width: tokens.spacingHorizontalXS,
    height: tokens.spacingHorizontalXS,
    borderRadius: '50%',
    flexShrink: 0,
    backgroundColor: tokens.colorPaletteGreenForeground1,
    '@media (forced-colors: active)': {
      backgroundColor: 'CanvasText',
    },
  },
  metaRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  metaItem: {
    color: tokens.colorNeutralForeground3,
  },
  connectionStringBlock: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    wordBreak: 'break-all',
    color: tokens.colorNeutralForeground2,
    backgroundColor: tokens.colorNeutralBackground2,
    padding: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  buttonRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalL,
  },
  inputRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  modeToggle: {
    paddingBottom: tokens.spacingVerticalXS,
  },
});

interface ObservabilityTabProps {
  projectName: string;
  canEdit: boolean;
}

const isApiError = (e: unknown): e is ObservabilityApiError =>
  !!e && typeof e === 'object' && 'status' in e && typeof (e as { status: unknown }).status === 'number';

const InfoBanner = () => (
  <MessageBar intent="info">
    <MessageBarBody>
      Changes will apply to all applications within a few minutes.
    </MessageBarBody>
  </MessageBar>
);

const formatSaveError = (e: unknown): { title: string; body?: string } => {
  if (isApiError(e)) {
    if (e.status === 412) {
      return { title: 'Cannot modify observability settings while the environment is being deleted.' };
    }
    if (e.status === 400) {
      return e.apiErrorMessage
        ? { title: 'The connection string was rejected by the server.', body: e.apiErrorMessage }
        : { title: 'The connection string was rejected by the server.' };
    }
    if (e.status === 401) {
      return { title: 'Your session expired. Please sign in again.' };
    }
  }
  return { title: 'Something went wrong saving the configuration. Please try again.' };
};

const ObservabilityTabInner = ({ projectName, canEdit }: ObservabilityTabProps) => {
  const styles = useStyles();
  const { trackEvent, trackException } = useTelemetry();

  const query = useObservabilityConfig(projectName);
  const mutation = useUpdateObservabilityConfig(projectName);
  const deleteMutation = useDeleteObservabilityConfig(projectName);

  const [pasteValue, setPasteValue] = useState('');
  const [validationMessage, setValidationMessage] = useState<string | undefined>(undefined);
  /** Tracks whether the user has interacted with the paste input. We defer
   *  showing validation errors until then so the field doesn't start in a
   *  red "required" state when first revealed. */
  const [pasteTouched, setPasteTouched] = useState(false);
  const [editing, setEditing] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [showInfoBanner, setShowInfoBanner] = useState(false);
  const [mode, setMode] = useState<InputMode>(() => {
    const stored = readStorage(modeStorageKey(projectName));
    return stored === 'paste' ? 'paste' : 'picker';
  });
  const initialSub = useMemo(() => readStorage(subStorageKey(projectName)) ?? undefined, [projectName]);

  const viewedRef = useRef(false);

  useEffect(() => {
    // Only emit the viewed event once the query has actually loaded — firing
    // on `!isLoading` would also fire on the error path and pollute the
    // `configured` dimension with load-failure noise.
    if (!viewedRef.current && query.isSuccess) {
      viewedRef.current = true;
      trackEvent('project.observability.viewed', {
        configured: query.data ? 'true' : 'false',
        readOnly: canEdit ? 'false' : 'true',
      });
    }
  }, [query.isSuccess, query.data, canEdit, trackEvent]);

  const handleModeChange = useCallback(
    (next: InputMode) => {
      setMode(next);
      writeStorage(modeStorageKey(projectName), next);
      setValidationMessage(undefined);
      if (next === 'picker') trackEvent('project.observability.picker.opened');
    },
    [projectName, trackEvent],
  );

  const isConfigured = !!query.data;
  const appInsights = query.data?.properties?.applicationInsights ?? null;
  const connectionString = appInsights?.connectionString;
  const updatedOn = query.data?.properties?.updatedOn;

  const showInputForm = (!isConfigured && canEdit) || (isConfigured && editing && canEdit);

  const handlePasteChange = (value: string) => {
    setPasteValue(value);
    if (!pasteTouched) setPasteTouched(true);
  };

  // Live validation: re-evaluate 300ms after the user stops typing so the
  // user gets actionable feedback before clicking Save. We skip validation
  // until the field has been touched at least once and we don't render the
  // "required" complaint for an empty just-revealed field.
  const debouncedPasteValue = useDebounce(pasteValue, 300);
  useEffect(() => {
    if (!pasteTouched) return;
    if (debouncedPasteValue === '') {
      setValidationMessage(undefined);
      return;
    }
    const result = validateAppInsightsConnectionString(debouncedPasteValue);
    setValidationMessage(result.ok ? undefined : result.message);
  }, [debouncedPasteValue, pasteTouched]);

  const applyConnectionString = async (connectionString: string, source: SavedSource) => {
    try {
      await mutation.mutateAsync({ kind: 'apply', connectionString });
      setPasteValue('');
      setPasteTouched(false);
      setValidationMessage(undefined);
      setEditing(false);
      setShowInfoBanner(true);
      trackEvent('project.observability.saved', { source });
    } catch (e) {
      const status = isApiError(e) ? e.status : undefined;
      trackEvent('project.observability.save_failed', status != null ? { status: String(status), source } : { source });
      trackException(new Error(`observability save failed: ${status ?? 'unknown'}`));
    }
  };

  const handleSavePaste = () => {
    const result = validateAppInsightsConnectionString(pasteValue);
    if (!result.ok) {
      setValidationMessage(result.message);
      trackEvent('project.observability.validation_failed');
      return;
    }
    void applyConnectionString(pasteValue, 'paste');
  };

  const handleConfirmPicker = (resource: AppInsightsResource) => {
    trackEvent('project.observability.picker.resource_selected');
    void applyConnectionString(resource.connectionString, 'picker');
  };

  const handleSubscriptionChange = (subscriptionId: string) => {
    writeStorage(subStorageKey(projectName), subscriptionId);
  };

  const handleCancelEdit = () => {
    setPasteValue('');
    setPasteTouched(false);
    setValidationMessage(undefined);
    setEditing(false);
  };

  const handleRemove = async () => {
    try {
      await deleteMutation.mutateAsync();
      setShowInfoBanner(true);
      setRemoveDialogOpen(false);
      trackEvent('project.observability.removed');
    } catch (e) {
      const status = isApiError(e) ? e.status : undefined;
      trackEvent('project.observability.remove_failed', status != null ? { status: String(status) } : undefined);
      trackException(new Error(`observability remove failed: ${status ?? 'unknown'}`));
    }
  };

  const saveErrorContent = useMemo(() => {
    if (mutation.isError) return formatSaveError(mutation.error);
    if (deleteMutation.isError) return formatSaveError(deleteMutation.error);
    return null;
  }, [mutation.isError, mutation.error, deleteMutation.isError, deleteMutation.error]);

  if (query.isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Skeleton aria-label="Loading observability configuration">
          <SkeletonItem size={32} />
          <SkeletonItem size={16} />
          <SkeletonItem size={64} />
        </Skeleton>
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className={styles.root}>
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Failed to load observability configuration</MessageBarTitle>
            Try again, or reload the page.
          </MessageBarBody>
          <MessageBarActions>
            <Button onClick={() => query.refetch()}>Retry</Button>
          </MessageBarActions>
        </MessageBar>
      </div>
    );
  }

  const inputForm = (
    <div className={styles.inputRow}>
      {canEdit && (
        <Field label="How do you want to connect?" className={styles.modeToggle}>
          <RadioGroup
            layout="horizontal"
            value={mode}
            onChange={(_, data) => handleModeChange(data.value as InputMode)}
            aria-label="Connection input mode"
          >
            <Radio value="picker" label="Select from my subscriptions" />
            <Radio value="paste" label="Paste connection string" />
          </RadioGroup>
        </Field>
      )}
      {mode === 'picker' ? (
        <AppInsightsResourcePicker
          {...(initialSub ? { initialSubscriptionId: initialSub } : {})}
          onSubscriptionChange={handleSubscriptionChange}
          onConfirm={handleConfirmPicker}
          saving={mutation.isPending}
        />
      ) : (
        <>
          <Field
            label={isConfigured ? 'New connection string' : 'Connection string'}
            required
            validationState={validationMessage ? 'error' : 'none'}
            hint='Format: "InstrumentationKey=<GUID>;IngestionEndpoint=https://..."'
            {...(validationMessage ? { validationMessage } : {})}
          >
            <Input
              value={pasteValue}
              onChange={(_, data) => handlePasteChange(data.value)}
              placeholder="InstrumentationKey=00000000-0000-0000-0000-000000000000;IngestionEndpoint=https://..."
              disabled={mutation.isPending}
            />
          </Field>
          <div className={styles.buttonRow}>
            <Button appearance="primary" onClick={handleSavePaste} disabled={mutation.isPending}>
              {mutation.isPending ? (isConfigured ? 'Updating…' : 'Saving…') : isConfigured ? 'Update' : 'Save'}
            </Button>
            {editing && (
              <Button appearance="subtle" icon={<DismissRegular />} onClick={handleCancelEdit} disabled={mutation.isPending}>
                Cancel
              </Button>
            )}
          </div>
        </>
      )}
      {mode === 'picker' && editing && (
        <div className={styles.buttonRow}>
          <Button appearance="subtle" icon={<DismissRegular />} onClick={handleCancelEdit} disabled={mutation.isPending}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className={styles.root}>
      <div className={styles.sectionHeader}>
        <Subtitle1 as="h2">Application Insights</Subtitle1>
        <Body1>
          Connect Application Insights to automatically send telemetry from all applications in this project.
        </Body1>
      </div>

      <Card className={styles.card}>
        {isConfigured ? (
          <>
            <div className={styles.statusRow}>
              <Badge
                appearance="filled"
                className={styles.statusBadge}
                aria-label="Application Insights status: Connected"
              >
                <span className={styles.statusDot} aria-hidden="true" />
                Connected
              </Badge>
              <Button
                appearance="subtle"
                icon={<ArrowClockwiseRegular />}
                aria-label="Refresh observability configuration"
                title="Refresh"
                onClick={() => query.refetch()}
                disabled={query.isFetching}
              />
              {query.isFetching && <Spinner size="tiny" aria-label="Refreshing" />}
            </div>

            <Field label="Connection string">
              <div className={styles.connectionStringBlock}>{connectionString}</div>
            </Field>

            <div className={styles.metaRow}>
              {updatedOn && (
                <Caption1 className={styles.metaItem}>Last updated: {getRelativeTime(updatedOn)}</Caption1>
              )}
            </div>

            {canEdit && !editing && (
              <div className={styles.buttonRow}>
                <Button
                  appearance="secondary"
                  icon={<EditRegular />}
                  onClick={() => setEditing(true)}
                  disabled={mutation.isPending || deleteMutation.isPending}
                >
                  Update
                </Button>
                <Button
                  appearance="secondary"
                  icon={<DeleteRegular />}
                  onClick={() => setRemoveDialogOpen(true)}
                  disabled={mutation.isPending || deleteMutation.isPending}
                >
                  Remove
                </Button>
              </div>
            )}

            {!canEdit && (
              <Caption1 className={styles.metaItem}>
                You don&apos;t have permission to modify this setting.
              </Caption1>
            )}
          </>
        ) : canEdit ? (
          <Body1>
            No Application Insights connection string has been configured for this project yet.
          </Body1>
        ) : (
          <Body1>Not configured.</Body1>
        )}

        {showInputForm && inputForm}

        {saveErrorContent && (
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>{saveErrorContent.title}</MessageBarTitle>
              {saveErrorContent.body}
            </MessageBarBody>
          </MessageBar>
        )}

        {showInfoBanner && !mutation.isError && !deleteMutation.isError && <InfoBanner />}
      </Card>

      <Dialog
        open={removeDialogOpen}
        onOpenChange={(_, data) => setRemoveDialogOpen(data.open)}
      >
        <DialogSurface aria-describedby="observability-remove-description">
          <DialogBody>
            <DialogTitle>Remove Application Insights?</DialogTitle>
            <DialogContent id="observability-remove-description">
              This will stop sending telemetry to Application Insights from all applications in this project. Are you sure?
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary" disabled={deleteMutation.isPending}>Cancel</Button>
              </DialogTrigger>
              <Button appearance="primary" onClick={handleRemove} disabled={deleteMutation.isPending}>
                {deleteMutation.isPending ? 'Removing…' : 'Remove'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export const ObservabilityTab = (props: ObservabilityTabProps) => (
  <FeatureErrorBoundary componentName="ObservabilityTab">
    <ObservabilityTabInner {...props} />
  </FeatureErrorBoundary>
);
